import { useEffect, useState, useRef, useCallback } from "react";
import StudentGrid from "../StudentGrid";
import { ExternalLink } from "../../../components";
import Modal from "../../../components/core/Modal";
import { useTutoringUser } from "../../../hooks";
import useRouter from "../../../hooks/useRouter";
import useAxios from "../../../hooks/useAxios";
import ChatbaseWidget from "../../../components/features/ChatbaseWidget";
import { BookOpenIcon, ClockIcon, UserGroupIcon } from "@heroicons/react/outline";
import { ContentContainer, PageContainer } from "../../../containers";
import { useDocumentTitle } from "../../../hooks/useDocumentTitle";
import Notification from "../Notification";
import TutorError from "../TutorError";
import LoaderPage from "../../utility/LoaderPage";
import useFomo from "../../../hooks/useFomo";
import Announcements from "./Announcements";
import ActionItems from "./ActionItems";
import HashImg from "../../../assets/images/hash.svg";
import BookImg from "../../../assets/images/book.svg";
import CalendarImg from "../../../assets/images/calendar.svg";
import OutLinkImg from "../../../assets/images/outgoing.svg";
import CloseIcon from "../../../assets/images/x.svg";

const defaultCardData = [
  {
    title: "Current Students",
    number: 0,
    img: HashImg,
    label: "First Match",
    value: "N/A",
  },
  {
    title: "Sessions",
    number: 0,
    img: BookImg,
    label: "Last 30 days",
    value: "0 Sessions",
  },
  {
    title: "Tutoring hours",
    number: "0h 0m",
    img: CalendarImg,
    label: "Last 30 days",
    value: "0h 0m",
  },
  {
    title: "Hours until promotion",
    number: "0h 0m",
    img: OutLinkImg,
    label: "Next title",
    value: "N/A",
  },
];

export default function Dashboard() {
  useDocumentTitle("Dashboard");
  const router = useRouter();
  const [, setStats] = useState([]);
  const { user, tutorError } = useTutoringUser();
  const { axiosInstance } = useAxios();
  const [studentInfo, setStudentInfo] = useState(null);
  const [cardData, setCardData] = useState(defaultCardData);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [dataLoaded, setDataLoaded] = useState({ announcements: false, actionItems: false });
  const announcementsRef = useRef();
  const actionItemsRef = useRef();

  useFomo();

  const markAnnouncementAsRead = useCallback(async () => {
    try {
      await axiosInstance.post('/tutor/mark-announcement-read');
      console.log('Announcement marked as read');
    } catch (error) {
      console.error('Error marking announcement as read:', error);
    }
  }, [axiosInstance]);

  const handleAnnouncementsDataLoaded = useCallback(() => {
    setDataLoaded((prev) => ({ ...prev, announcements: true }));
  }, []);

  const handleActionItemsDataLoaded = useCallback(() => {
    setDataLoaded((prev) => ({ ...prev, actionItems: true }));
  }, []);

  const handleAnnouncementModal = () => {
    
    setShowAnnouncementModal(false);
  };
  useEffect(() => {
    if (!user) return;
    const students = user.correspondents || [];
    const tutoringStats = [
      {
        name: "Students",
        icon: UserGroupIcon,
        stat: students.length || 0,
      },
      {
        name: "Sessions",
        icon: BookOpenIcon,
        stat: user.totalSessions || 0,
      },
      {
        name: "Minutes",
        icon: ClockIcon,
        stat: user.minutesTutored || 0,
      },
    ];
    setStats(tutoringStats);
  }, [user, user?.correspondents]);

  useEffect(() => {
    if (user && user.hasNewAnnouncement && dataLoaded.announcements && dataLoaded.actionItems) {
      const timer = setTimeout(() => {
        setShowAnnouncementModal(true);
        markAnnouncementAsRead();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [user, user?.hasNewAnnouncement, dataLoaded.announcements, dataLoaded.actionItems, markAnnouncementAsRead]);

  // Update card data when user stats are available
  useEffect(() => {
    if (!user || !user.stats) return;

    const stats = user.stats;

    const updatedCardData = [
      {
        title: "Current Students",
        number: stats?.currentStudents || 0,
        img: HashImg,
        label: "First Match",
        value: stats?.firstMatch || "N/A",
      },
      {
        title: "Sessions",
        number: stats?.sessions || 0,
        img: BookImg,
        label: "Last 30 days",
        value: `${stats?.last30DaysSessions || 0} Sessions`,
      },
      {
        title: "Tutoring hours",
        number: stats?.tutoringHours || "0h 0m",
        img: CalendarImg,
        label: "Last 30 days",
        value: stats?.last30DaysTutoringHours || "0h 0m",
      },
      {
        title: "Hours until promotion",
        number: stats?.hoursUntilPromotion || "0h 0m",
        img: OutLinkImg,
        label: "Next title",
        value: stats?.nextTitle || "N/A",
      },
    ];

    setCardData(updatedCardData);
  }, [user, user?.stats]);

  if (tutorError) {
    return <TutorError />;
  } else if (!user) {
    return <LoaderPage />;
  } else {
    return (
      <>
        <ChatbaseWidget />
        <PageContainer>
          <ContentContainer>
            <div className="hidden  md:grid-cols-2 lg:grid-cols-4 gap-4 -mt-14 pb-10 md:grid">
              {cardData.map((card, index) => {
                return (
                  <div key={index} className="p-4 border border-[#E5E7EB] rounded-xl bg-white">
                    <div className="flex gap-3 items-start justify-between">
                      <div>
                        <div className="text-[#6B7280] text-xs">{card.title}</div>
                        <div className="font-semibold text-[#1F2937] text-xl pt-1">{card.number}</div>
                      </div>
                      <img src={card.img} alt="card" />
                    </div>
                    <div className="pt-3 text-[#1F2937] text-xs">
                      <span className="text-[#6B7280]">{card.label}:</span> {card.value}
                    </div>
                  </div>
                );
              })}
            </div>
          </ContentContainer>
          <ContentContainer className="pb-3">
            <div className="text-black text-2xl font-semibold pb-3">Students</div>
            <StudentGrid setStudentInfo={setStudentInfo} />
          </ContentContainer>
          <ContentContainer className="sm:block lg:block md:block">
            <div className="text-black text-2xl font-semibold md:py-7 py-5 md:text-left text-center">What's New</div>

            <div className="px-0 sm:px-5">
              <Announcements
                ref={announcementsRef}
                onDataLoaded={handleAnnouncementsDataLoaded}
              />
              <ActionItems
                ref={actionItemsRef}
                initialShowModal={router.query.showModal === "true"}
                onDataLoaded={handleActionItemsDataLoaded}
              />
            </div>
          </ContentContainer>
        </PageContainer>
        <Notification user={user} />
        {studentInfo && (
          <Modal
            close={() => {
              setStudentInfo(null);
            }}
            title="Student Information">
            <StudentModal studentInfo={studentInfo} setStudentInfo={setStudentInfo} />
          </Modal>
        )}
        {showAnnouncementModal && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-3xl shadow-lg w-full max-w-[950px] transform transition-all duration-300 relative z-60 p-6 md:p-10 h-[80vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}>
              <h1 className="text-lg md:text-[24px] font-semibold text-[#0C6980] leading-normal font-inter pb-7">What's New</h1>
              <button
                className="absolute right-3 top-3 md:right-[30px] md:top-[30px] w-10 h-10 rounded-full flex items-center justify-center"
                onClick={handleAnnouncementModal}>
                <img src={CloseIcon} alt="Close Icon" />
              </button>
              <div className="pb-5">
                <Announcements disableModal={true} />
              </div>
              <ActionItems initialShowModal={router.query.showModal === "true"} hideFooter={true} disableModal={true} />
            </div>
          </div>
        )}
      </>
    );
  }
}

const StudentModal = ({ studentInfo, setStudentInfo }) => {
  const meetingID = studentInfo.Tutoring_Code;
  const meetingLink = "https://student-portal.stepuptutoring.org/student/" + meetingID;

  const filteredInfo = [
    "First_Name",
    "Last_Name",
    "Guardian_First_Name",
    "Guardian_Last_Name",
    "School_Text",
    "District_Text",
    "Availability",
  ];

  const copyToClipboard = () => {
    navigator.clipboard.writeText(meetingLink);
    alert("Meeting link copied to clipboard");
  };

  return (
    <div className="max-h-[60vh] overflow-y-auto">
      {meetingLink && (
        <>
          <div className="font-bold mb-2">Student Meeting Link</div>
          <div className="mb-4 flex items-center">
            <ExternalLink
              href={meetingLink}
              className="flex-1 p-2 border rounded mr-2 overflow-hidden text-ellipsis whitespace-nowrap">
              {meetingLink}
            </ExternalLink>
            <button onClick={copyToClipboard} className="p-2 bg-gray-400 text-white rounded">
              Copy
            </button>
          </div>
        </>
      )}
      <ul>
        {filteredInfo.map((key) => (
          <li className="mt-3" key={key} style={{ wordBreak: "break-all", overflowWrap: "break-word" }}>
            <span className="font-bold">{key.replace(/_/g, " ")}:</span> {studentInfo[key]}
          </li>
        ))}
      </ul>
    </div>
  );
};
